import { useGoBack } from 'hooks/useGoBack';
import React, { useCallback, useContext } from 'react';
import { convertUndefinedFields } from 'utils/helpers';
import {
	IEditModifierGroupsContext,
	EditModifierGroupsContext,
} from '../../editModifierGroupsState/editModifierGroupsContext';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import ModalSentErrorWithInfo from './ModalSentErrorWithInfo/ModalSentErrorWithInfo';
import { AppContext, IAppContext } from 'appState/appContext';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { updateModifierGroups } from 'backend/modifierGroups';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		refetchData,
		showErrorModalWithInfo,
		dataForSubmit,
		errorResponseData,
		typeOfModalWindow,
		fetchedRestaurants,
		fetchedDishes,
	} = useContext<IEditModifierGroupsContext>(EditModifierGroupsContext);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow || errorResponseData });
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/menu/modifier-groups');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				changeTypeOfModalWindow(null);
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateModifierGroups(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.MODIFIER_GROUP_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={handleRedirect}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleUpdateData}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'defaultTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			<ModalSentErrorWithInfo
				onConsentEvent={handleCloseModalWithInfo}
				isVisible={!!errorResponseData}
				errorResponseData={errorResponseData}
				fetchedDishes={fetchedDishes}
				fetchedRestaurants={fetchedRestaurants}
			/>
		</div>
	);
};

export default ModalWindows;
